'use client'

import { GroupClassAttendanceStatus } from '@fingertip/creator-proto/gen/fingertip/common/enum/v1/group_class_attendance_status_pb'
import { EventType } from '@fingertip/creator-proto/gen/fingertip/common/type/v1/event_type_pb'
import { GroupClassAttendance } from '@fingertip/creator-proto/gen/fingertip/common/type/v1/group_class_attendance_pb'
import { GroupClass } from '@fingertip/creator-proto/gen/fingertip/common/type/v1/group_class_pb'
import { SiteContact } from '@fingertip/creator-proto/gen/fingertip/common/type/v1/site_contact_pb'
import { StoreConfig } from '@fingertip/creator-proto/gen/fingertip/common/type/v1/store_config_pb'
import { SquareArrowTopRightIcon } from '@fingertip/icons'
import { format, toZonedTime } from 'date-fns-tz'
import { observer } from 'mobx-react-lite'
import dynamic from 'next/dynamic'
import Link from 'next/link'
import React, { useCallback, useContext, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { BookerSuccessCancelAttendanceDialog } from '@/components/booking/booker/dialog/booker-success-cancel-attendance-dialog'
import { BookingLocation } from '@/components/booking/booking-location'
import { cancellationReasons } from '@/components/booking/booking-page/cancellation-reason-form'
import { TooltipWrapper } from '@/components/shared/tooltip-wrapper'
import { Button } from '@/components/ui/button'
import { ResponsiveSheet } from '@/components/ui/responsive-sheet'
import { formatTimezoneLabel } from '@/lib/dates/time-zone'
import { useBreakpoint } from '@/lib/hooks/use-breakpoint'
import { useTimedDate } from '@/lib/hooks/use-timed-date'
import { RootStoreContext } from '@/lib/stores/root-store'
import { cn } from '@/lib/utils/cn'

const AddToCalendarButton = dynamic(
  () =>
    import('add-to-calendar-button-react').then((mod) => ({
      default: mod.AddToCalendarButton,
    })),
  {
    ssr: false,
  },
)

type Props = {
  groupClassAttendance: GroupClassAttendance
  groupClass: GroupClass
  groupClassAttendanceContact: SiteContact
  eventType: EventType
  storeConfig?: StoreConfig
  siteSlug: string
}

export const BookerStageAttendanceSuccess = observer(
  ({
    storeConfig,
    groupClass,
    groupClassAttendance,
    groupClassAttendanceContact,
    eventType,
    siteSlug,
  }: Props) => {
    const breakpoint = useBreakpoint()
    const { t } = useTranslation()
    const [cancelSheetOpen, setCancelSheetOpen] = useState(false)

    const {
      bookerStore: { timeZone },
    } = useContext(RootStoreContext)

    const zonedStartTime = groupClass?.startTime
      ? toZonedTime(groupClass.startTime.toDate(), timeZone)
      : undefined

    const zonedEndTime = groupClass?.endTime
      ? toZonedTime(groupClass.endTime.toDate(), timeZone)
      : undefined

    const formattedDate = zonedStartTime
      ? format(zonedStartTime, 'EEEE, MMM do, yyyy', { timeZone })
      : undefined

    const formattedStartTime = zonedStartTime
      ? format(zonedStartTime, 'h:mm aaa', { timeZone })
      : undefined

    const formattedEndTime = zonedEndTime
      ? format(zonedEndTime, 'h:mm aaa', { timeZone })
      : undefined

    const addToCalendarConfig: any = {
      name: eventType?.name || '',
      description: eventType?.description || '',
      startDate: zonedStartTime
        ? format(zonedStartTime, 'yyyy-MM-dd', { timeZone })
        : undefined,
      startTime: zonedStartTime
        ? format(zonedStartTime, 'HH:mm', { timeZone })
        : undefined,
      endDate: zonedEndTime
        ? format(zonedEndTime, 'yyyy-MM-dd', { timeZone })
        : undefined,
      endTime: zonedEndTime
        ? format(zonedEndTime, 'HH:mm', { timeZone })
        : undefined,
      timeZone,
      location: groupClass?.location,
      options: ['Google', 'Apple', 'iCal', 'Outlook.com'],
    }

    const handleCancel = useCallback(() => {
      setCancelSheetOpen(true)
    }, [setCancelSheetOpen])

    const attendanceHref = useMemo(() => {
      if (groupClassAttendance?.id) {
        return `/${siteSlug}/booking?attendanceId=${groupClassAttendance.id}&stage=SUCCESS`
      } else {
        return '/'
      }
    }, [groupClassAttendance?.id, siteSlug])

    const currentDate = useTimedDate()

    const cancelRescheduleButtonsShown = useMemo(() => {
      if (groupClass.startTime) {
        return currentDate.getTime() < groupClass.startTime.toDate().getTime()
      }
      return false
    }, [groupClass.startTime, currentDate])

    const cancellationFeeEnabled = useMemo(() => {
      return (
        !!groupClass.cancellationLateAfterDate &&
        groupClass.cancellationFeeInCents > 0
      )
    }, [
      groupClass.cancellationFeeInCents,
      groupClass.cancellationLateAfterDate,
    ])

    const cancellationFeeApplies = useMemo(() => {
      return (
        !!groupClass.cancellationLateAfterDate &&
        groupClass.cancellationFeeInCents > 0 &&
        currentDate.getTime() >
          groupClass.cancellationLateAfterDate.toDate().getTime()
      )
    }, [
      groupClass.cancellationFeeInCents,
      groupClass.cancellationLateAfterDate,
      currentDate,
    ])

    const cancellationLabel = useMemo(() => {
      const reason = cancellationReasons.find(
        (reason) => reason.value === groupClassAttendance.cancellationReason,
      )
      return reason
        ? `${reason.label}`
        : `${groupClassAttendance.cancellationReason}`
    }, [groupClassAttendance.cancellationReason])

    const rejectionLabel = useMemo(() => {
      const reason = cancellationReasons.find(
        (reason) => reason.value === groupClassAttendance.rejectionReason,
      )
      return reason
        ? `${reason.label}`
        : `${groupClassAttendance.rejectionReason}`
    }, [groupClassAttendance.rejectionReason])

    return (
      <div>
        <div className="mb-4 flex flex-col items-center justify-between">
          {groupClassAttendance?.status ===
            GroupClassAttendanceStatus.ACCEPTED && (
            <>
              <div className="max-w-[500px]">
                <Link href={attendanceHref}>
                  <div className="h2 mb-4 flex flex-row items-center justify-center gap-2 text-foreground">
                    {t('this_event_is_scheduled')}
                    <SquareArrowTopRightIcon />
                  </div>
                </Link>
                <p className="mb-4 text-center text-foreground">
                  {t(
                    'we_emailed_you_and_the_other_attendees_a_calendar_invitation_with_all_the_details',
                  )}
                </p>
              </div>
              <AddToCalendarButton
                styleDark="border-radius: 50% 50%; --btn-background: rgb(249, 88, 40); --btn-border: rgb(249, 88, 40); --btn-text: #fff;"
                styleLight="border-radius: 50% 50%; --btn-background: rgb(249, 88, 40); --btn-border: rgb(249, 88, 40); --btn-text: #fff;"
                buttonStyle="default"
                trigger="click"
                hideIconButton
                hideBackground
                hideCheckmark
                hideBranding
                label={t('add_to_calendar')}
                lightMode="bodyScheme"
                {...addToCalendarConfig}
              />
            </>
          )}

          {groupClassAttendance?.status ===
            GroupClassAttendanceStatus.WAITLIST && (
            <div className="max-w-[500px]">
              <Link href={attendanceHref}>
                <div className="h2 mb-4 flex flex-row items-center justify-center gap-2 text-foreground">
                  {t('You are on the waitlist')}
                  <SquareArrowTopRightIcon />
                </div>
              </Link>
              <p className="mb-4 text-center text-foreground">
                {t(
                  'Thanks for joining the waitlist. While it is not guaranteed, we will do our best to accommodate you if a spot opens up.',
                )}
              </p>
            </div>
          )}

          {groupClassAttendance?.status ===
            GroupClassAttendanceStatus.PENDING_CONFIRMATION && (
            <div className="max-w-[500px]">
              <Link href={attendanceHref}>
                <div className="h2 mb-4 flex flex-row items-center justify-center gap-2 text-foreground">
                  {t('You have been accepted')}
                  <SquareArrowTopRightIcon />
                </div>
              </Link>
              <p className="mb-4 text-center text-foreground">
                {t(
                  "Thanks for waiting, we are almost there! We've emailed you this unique confirmation link to proceed.",
                )}
              </p>
            </div>
          )}

          {groupClassAttendance?.status ===
            GroupClassAttendanceStatus.CANCELLED && (
            <div className="max-w-[500px]">
              <p className="h2 mb-4 text-center text-foreground">
                {t('thanks_for_letting_u')}
              </p>
              <p className="mb-4 text-center text-foreground">
                {t('you_are_no_longer_at')}
              </p>
              {!!cancellationLabel && (
                <div className="mt-4 flex flex-row gap-2">
                  <span>{t('Reason') + ': '}</span>
                  <span className="italic text-muted-foreground">
                    {cancellationLabel}
                  </span>
                </div>
              )}
            </div>
          )}

          {groupClassAttendance?.status ===
            GroupClassAttendanceStatus.REJECTED && (
            <div className="max-w-[500px]">
              <h1 className="h2 mb-4 text-center text-foreground">
                {t('Request declined')}
              </h1>
              <p>
                {t(
                  "You were on the waitlist for this event, but unfortunately, we couldn't still accommodate you.",
                )}
              </p>

              {!!rejectionLabel && (
                <div className="mt-4 flex flex-row gap-2">
                  <span>{t('Reason') + ': '}</span>
                  <span className="italic text-muted-foreground">
                    {rejectionLabel}
                  </span>
                </div>
              )}
            </div>
          )}

          {groupClassAttendance?.status ===
            GroupClassAttendanceStatus.COMPLETED && (
            <div className="max-w-[500px]">
              <p className="h2 mb-4 text-center text-foreground">
                {t('you_attended_this_ev')}
              </p>
              <p className="mb-4 text-center text-foreground">
                {t('thanks_for_coming_se')}
              </p>
            </div>
          )}

          {groupClassAttendance?.status ===
            GroupClassAttendanceStatus.REFUNDED && (
            <div className="max-w-[500px]">
              <h1 className="h2 mb-4 text-center text-foreground">
                {t('this_event_has_been_')}
              </h1>
            </div>
          )}

          <div className="divider" />

          <div className="grid grid-cols-3 gap-x-2 gap-y-8 p-4 text-left text-foreground">
            <div>{t('what')}</div>
            <div className="col-span-2">
              <p>{eventType.name}</p>
              {groupClass.notes && groupClass.notes.length > 0 && (
                <div className="mt-2 text-sm text-muted-foreground">
                  <TooltipWrapper message={groupClass.notes}>
                    {t('view_event_notes')}
                  </TooltipWrapper>
                </div>
              )}
            </div>

            <div>{t('when')}</div>
            <div
              className={cn('col-span-2', {
                'line-through':
                  groupClassAttendance?.status ===
                    GroupClassAttendanceStatus.CANCELLED ||
                  groupClassAttendance?.status ===
                    GroupClassAttendanceStatus.REJECTED,
              })}
            >
              <p>{formattedDate}</p>
              <p>{`${formattedStartTime} – ${formattedEndTime}`}</p>
              <p>{formatTimezoneLabel(timeZone)}</p>
            </div>

            <div>{t('where')}</div>
            <div className="col-span-2">
              <BookingLocation
                location={groupClass?.location}
                locationType={eventType.locations[0].type}
                hideDetails={
                  groupClassAttendance?.status !==
                  GroupClassAttendanceStatus.ACCEPTED
                }
              />
            </div>

            <div>{t('who')}</div>
            <div className="col-span-2">
              {!!groupClassAttendanceContact.name && (
                <p>{groupClassAttendanceContact.name}</p>
              )}
              {!!groupClassAttendanceContact.email && (
                <p>{groupClassAttendanceContact.email}</p>
              )}
              {groupClassAttendance.notes &&
                groupClassAttendance.notes.length > 0 && (
                  <div className="mt-2 text-sm text-muted-foreground">
                    <TooltipWrapper message={groupClassAttendance.notes}>
                      {t('view_booking_notes')}
                    </TooltipWrapper>
                  </div>
                )}
            </div>

            {/* This section is for host details */}
            {storeConfig?.hostDetails &&
              storeConfig?.hostDetails.length > 0 && (
                <>
                  <div>{t('host')}</div>
                  <div className="col-span-2">
                    <p className="whitespace-pre-wrap">
                      {storeConfig?.hostDetails}
                    </p>
                  </div>
                </>
              )}
          </div>

          <div className="divider" />

          {cancelRescheduleButtonsShown &&
            groupClassAttendance?.status !==
              GroupClassAttendanceStatus.WAITLIST &&
            groupClassAttendance?.status !==
              GroupClassAttendanceStatus.COMPLETED &&
            groupClassAttendance?.status !==
              GroupClassAttendanceStatus.CANCELLED &&
            groupClassAttendance?.status !==
              GroupClassAttendanceStatus.REJECTED && (
              <div className="flex flex-col">
                <div className="mb-4 text-center text-muted-foreground">
                  <div className="mb-4">{t('need_to_make_a_change')}</div>
                  {cancellationFeeEnabled &&
                    !cancellationFeeApplies &&
                    groupClass.cancellationLateAfterDate && (
                      <div className="mb-4 flex flex-col">
                        <span>{t('cancel_for_free_befo')}</span>
                        <span>
                          {format(
                            toZonedTime(
                              groupClass.cancellationLateAfterDate.toDate(),
                              timeZone,
                            ),
                            `EEE, MMM do, yyyy 'at' h:mm aaa`,
                            { timeZone },
                          )}
                        </span>
                      </div>
                    )}
                </div>

                <div className="flex items-center justify-center space-x-2">
                  <Button
                    size="sm"
                    type="button"
                    variant="secondary"
                    onClick={handleCancel}
                  >
                    {t('cancel')}
                  </Button>
                </div>
              </div>
            )}
        </div>

        <ResponsiveSheet
          open={cancelSheetOpen}
          modal={breakpoint !== 'sm'}
          onOpenChange={(open) => {
            setCancelSheetOpen(open)
          }}
        >
          <BookerSuccessCancelAttendanceDialog
            storeConfig={storeConfig}
            groupClass={groupClass}
            groupClassAttendance={groupClassAttendance}
            siteSlug={siteSlug}
            cancellationFeeEnabled={cancellationFeeEnabled}
            cancellationFeeApplies={cancellationFeeApplies}
            onClose={() => {
              setCancelSheetOpen(false)
            }}
          />
        </ResponsiveSheet>
      </div>
    )
  },
)
